import { useEffect } from "react";
import { addPollingIds, getPollingIds } from "@root/utils/pollingQueue";
import { useAssessmentPollingQueue } from "./useAssessmentPollingQueue";
import { createItemUpdateHandler } from "@root/utils/helpers";
import { RISK_ASSESSMENT_POLLING_INTERVAL } from "@root/constants/common";

interface UseAssessmentPollingSetupOptions<T extends { id: string | number }> {
  items: T[];
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  processingStatuses: number[];
  dependencies?: React.DependencyList;
}

/**
 * Custom hook that sets up assessment polling with automatic item updates
 * Handles both seeding polling from current items and resuming from localStorage
 */
export function useAssessmentPollingSetup<T extends { id: string | number }>({
  items,
  setItems,
  processingStatuses,
  dependencies = [],
}: UseAssessmentPollingSetupOptions<T>) {
  // Create update handler that merges updated items into state
  const onItemUpdate = createItemUpdateHandler(setItems);

  // Initialize polling queue
  const { start: startPollingQueue } = useAssessmentPollingQueue({
    onItemUpdate,
    intervalMs: RISK_ASSESSMENT_POLLING_INTERVAL,
  });

  // On mount: resume polling for any saved ids immediately
  useEffect(() => {
    const saved = getPollingIds();
    if (saved && saved.length > 0) {
      startPollingQueue(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed and (re)start polling for items in processing statuses
  useEffect(() => {
    try {
      const processingIds =
        (items || [])
          .filter(
            (item: any) =>
              item?.id && processingStatuses.includes(Number(item?.status))
          )
          .map((item: any) => String(item.id)) || [];

      // Persist any newly found non-completed ids
      if (processingIds.length > 0) {
        addPollingIds(processingIds);
      }

      const saved = getPollingIds();
      const allIds = Array.from(
        new Set([...(processingIds || []), ...(saved || [])])
      );

      if (allIds.length > 0) {
        startPollingQueue(allIds);
      }
    } catch {
      // no-op
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, ...dependencies]);

  return { startPollingQueue };
}
